import { useCallback, useEffect, useRef, useState } from "react";
import type { GroupingSource } from "services/metadoc-api";

type UseGroupingSourceArgs = {
  metadocId?: string;
  hasAIGroups: boolean;
  isDocumentGroupsLoading: boolean;
  isDocumentGroupsFetching: boolean;
};

/* manages Grouping preference (OPS / AI toggle) and fallback behavior */
export const useGroupingSource = ({
  metadocId,
  hasAIGroups,
  isDocumentGroupsLoading,
  isDocumentGroupsFetching,
}: UseGroupingSourceArgs) => {
  const previousMetadocIdRef = useRef<string | undefined>(metadocId);
  const hasUserToggledRef = useRef(false);

  const [preferredGroupingSource, setPreferredGroupingSource] =
    useState<GroupingSource>("OPS");
  const [groupingSource, setGroupingSource] = useState<GroupingSource>("OPS");
  const [shouldAutoSwitchToAI, setShouldAutoSwitchToAI] = useState(false);

  // reset state when navigating to a different metadoc
  useEffect(() => {
    if (previousMetadocIdRef.current !== metadocId) {
      setPreferredGroupingSource("OPS");
      setGroupingSource("OPS");
      setShouldAutoSwitchToAI(false);
      hasUserToggledRef.current = false;
      previousMetadocIdRef.current = metadocId;
    }
  }, [metadocId]);

  const handleGroupingSourceChange = useCallback(
    (source: GroupingSource) => {
      if (source === preferredGroupingSource && source === groupingSource) {
        return;
      }

      hasUserToggledRef.current = true;
      setPreferredGroupingSource(source);
      setGroupingSource(source);
    },
    [groupingSource, preferredGroupingSource],
  );

  // auto-switch to AI on initial load when AI groups are available
  useEffect(() => {
    if (isDocumentGroupsLoading || isDocumentGroupsFetching) {
      return;
    }
    if (hasAIGroups && !hasUserToggledRef.current) {
      setPreferredGroupingSource("AI");
      setGroupingSource("AI");
    }
  }, [hasAIGroups, isDocumentGroupsLoading, isDocumentGroupsFetching]);

  // auto-fallback to OPS if user is on AI but AI groups become empty
  useEffect(() => {
    if (isDocumentGroupsLoading || isDocumentGroupsFetching) {
      return;
    }
    if (
      groupingSource === "AI" &&
      !hasAIGroups &&
      preferredGroupingSource !== "AI"
    ) {
      setGroupingSource("OPS");
    }
  }, [
    groupingSource,
    hasAIGroups,
    isDocumentGroupsLoading,
    isDocumentGroupsFetching,
    preferredGroupingSource,
  ]);

  // auto-switch to AI when AI groups appear after job completion
  useEffect(() => {
    if (shouldAutoSwitchToAI && hasAIGroups) {
      setGroupingSource("AI");
      setShouldAutoSwitchToAI(false);
    }
  }, [shouldAutoSwitchToAI, hasAIGroups]);

  const triggerAutoSwitchToAI = useCallback(() => {
    setShouldAutoSwitchToAI(true);
  }, []);

  return {
    groupingSource,
    handleGroupingSourceChange,
    triggerAutoSwitchToAI,
  };
};
